/**
 * HistoryManager — Simple undo/redo stack for application state snapshots.
 *
 * Stores JSON-serializable snapshots and provides undo/redo navigation.
 * Coalesces rapid changes by deduplicating consecutive identical snapshots.
 *
 * @module HistoryManager
 */
export class HistoryManager {
  /**
   * @param {number} [maxSize=50] Maximum number of snapshots to retain
   */
  constructor(maxSize = 50) {
    this.#stack = [];
    this.#cursor = -1;
    this.#maxSize = maxSize;
  }

  /**
   * Push a new state snapshot onto the history stack.
   * Any states after the current cursor (redo history) are discarded.
   * Consecutive identical snapshots are coalesced.
   * @param {*} state JSON-serializable state
   */
  push(state) {
    const serialized = JSON.stringify(state);

    // Coalesce: skip if identical to current state
    if (this.#cursor >= 0 && this.#stack[this.#cursor].serialized === serialized) {
      return;
    }

    // Discard any redo history
    this.#stack = this.#stack.slice(0, this.#cursor + 1);

    this.#stack.push({ state, serialized });
    this.#cursor = this.#stack.length - 1;

    // Enforce max size
    if (this.#stack.length > this.#maxSize) {
      this.#stack.shift();
      this.#cursor--;
    }
  }

  /**
   * Undo — return the previous state, or null if at the beginning.
   * @returns {*|null}
   */
  undo() {
    if (this.#cursor <= 0) return null;
    this.#cursor--;
    return this.#stack[this.#cursor].state;
  }

  /**
   * Redo — return the next state, or null if at the end.
   * @returns {*|null}
   */
  redo() {
    if (this.#cursor >= this.#stack.length - 1) return null;
    this.#cursor++;
    return this.#stack[this.#cursor].state;
  }

  /**
   * Whether undo is available.
   * @returns {boolean}
   */
  canUndo() {
    return this.#cursor > 0;
  }

  /**
   * Whether redo is available.
   * @returns {boolean}
   */
  canRedo() {
    return this.#cursor < this.#stack.length - 1;
  }

  /**
   * Clear all history.
   */
  clear() {
    this.#stack = [];
    this.#cursor = -1;
  }

  // ── Private ──────────────────────────────────────────────────────

  /** @type {Array<{ state: *, serialized: string }>} */
  #stack;

  /** @type {number} */
  #cursor;

  /** @type {number} */
  #maxSize;
}
